import { useState } from "react";
import { Link } from "react-router-dom";
import { Train, Calendar, Bell, Shield, ChevronRight, Clock, Users, MapPin, Play, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

const LandingPage = ({ onLogin, onDemoLogin }) => {
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleDemoLogin = async () => {
    setIsLoggingIn(true);
    await onDemoLogin();
    setIsLoggingIn(false);
  };

  const features = [
    {
      icon: Train,
      title: "Real-time PNR Tracking",
      description: "Track unlimited PNRs with automatic status updates every hour"
    },
    {
      icon: Calendar,
      title: "Google Calendar Sync",
      description: "Automatically sync journey details to your calendar with reminders"
    },
    {
      icon: Bell,
      title: "Status Alerts",
      description: "Get notified instantly when your ticket status changes"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your data is encrypted and never shared with third parties"
    }
  ];

  const stats = [
    { value: "10K+", label: "PNRs Tracked" },
    { value: "99.9%", label: "Uptime" },
    { value: "1hr", label: "Update Frequency" }
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-zinc-950/60 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center">
                <Train className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>
                RailSync
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button 
                  variant="ghost"
                  data-testid="header-email-login-btn"
                  className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Sign in
                </Button>
              </Link>
              <Button 
                onClick={onLogin}
                data-testid="header-login-btn"
                className="bg-indigo-500 hover:bg-indigo-400 text-white rounded-lg shadow-[0_0_15px_rgba(99,102,241,0.2)] btn-press"
              >
                Sign in with Google
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 empty-state-bg opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-950/80 to-zinc-950" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm mb-8">
              <Clock className="w-4 h-4" />
              <span>Auto-updates every hour</span>
            </div>
            
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-50 tracking-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Track Your Indian Railway{" "}
              <span className="gradient-text">PNR Status</span>{" "}
              Effortlessly
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
              Add your PNR numbers once, and we'll automatically track status changes, 
              sync to your Google Calendar, and keep you informed throughout your journey.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={onLogin}
                data-testid="hero-get-started-btn"
                size="lg"
                className="bg-indigo-500 hover:bg-indigo-400 text-white rounded-lg text-lg px-8 shadow-[0_0_30px_rgba(99,102,241,0.3)] btn-glow btn-press"
              >
                Sign in with Google
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                onClick={handleDemoLogin}
                disabled={isLoggingIn}
                variant="outline"
                size="lg"
                data-testid="hero-demo-btn"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-lg text-lg px-8"
              >
                <Play className="w-5 h-5 mr-2" />
                {isLoggingIn ? "Starting..." : "Try Demo"}
              </Button>
            </div>

            {/* Telegram Bot Link */}
            <div className="mt-6 text-center">
              <a 
                href="https://t.me/Dhavalrailbot" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors"
              >
                <Send className="w-4 h-4" />
                <span>Or track via Telegram Bot @Dhavalrailbot</span>
              </a>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-zinc-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-2xl sm:text-3xl font-semibold text-zinc-50 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Everything You Need for PNR Management
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              A complete solution for tracking your Indian Railway bookings with smart automation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6 card-hover"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-lg font-medium text-zinc-50 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-2xl sm:text-3xl font-semibold text-zinc-50 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              How It Works
            </h2>
            <p className="text-zinc-400">Three simple steps to never miss a status update</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", icon: Users, title: "Sign In", desc: "Connect with your Google account for secure access and calendar sync" },
              { step: "02", icon: MapPin, title: "Add PNRs", desc: "Enter your 10-digit PNR numbers and we'll fetch all the details" },
              { step: "03", icon: Bell, title: "Stay Updated", desc: "Receive automatic updates and calendar events for your journeys" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-zinc-800/50 absolute -top-4 -left-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {item.step}
                </div>
                <div className="relative pt-8 pl-4">
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-medium text-zinc-50 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {item.title}
                  </h3>
                  <p className="text-zinc-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-zinc-950" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Ready to Simplify Your Railway Travel?
          </h2>
          <p className="text-lg text-zinc-400 mb-10">
            Join thousands of travelers who trust RailSync for their PNR management
          </p>
          <Button 
            onClick={handleDemoLogin}
            disabled={isLoggingIn}
            data-testid="cta-get-started-btn"
            size="lg"
            className="bg-indigo-500 hover:bg-indigo-400 text-white rounded-lg text-lg px-10 shadow-[0_0_30px_rgba(99,102,241,0.4)] btn-glow btn-press"
          >
            {isLoggingIn ? "Starting Demo..." : "Start Tracking Now"}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
                <Train className="w-4 h-4 text-white" />
              </div>
              <span className="text-zinc-400">RailSync</span>
            </div>
            <p className="text-sm text-zinc-500">
              © 2026 RailSync. Not affiliated with Indian Railways.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
